"use client";

import Banner from "../shared/Banner";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import { useContext, useEffect, useRef, useState } from "react";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import TitleDescAndSideItems from "../shared/titleDescAndSideItems/TitleDescAndSideItems";
import NeighborhoodsDetailsSwiper from "../neighborhoodsDetailsPage/NeighborhoodsDetailsSwiper";
import OSectionWithNoImages from "../aboutUsPage/OSectionWithNoImages";
import AccordionSection from "../aboutUsPage/AccordionSection";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react"
import { FreeMode, Navigation } from "swiper/modules"
import ImageWithLoader from "../shared/imageWithLoader/ImageWithLoader";
import Popup from "../shared/Popup/Popup";
import CircleIconButton from "../shared/CircleIconButton";
import OneDirectionArrow from "../shared/icons/OneDirectionArrow";
import CloseWidthImage from "../shared/icons/CloseWidthImage";
import { usePopupContext } from "@/app/store/context/popupContext";
import dynamic from 'next/dynamic';
import WhatsHappening from "../homePage/whatsHappening/WhatsHappening";
import MediaCenterDetails from "../mediaCenterPage/MediaCenterDetails";
import gsap from "gsap";


const ReactPlayerComp = dynamic(() => import('react-player'), {
    ssr: false,
});

const SportsClubPageComponent = ({ data }: any) => {
    const { isIpad, isMobile } = useContext(screenSizeContext);
    const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
    const { imgSrc, videoSrc } = useMediaGetter(data?.sections?.banners?.[0]?.media);
    const fullScreenSwiperRef = useRef<SwiperRef>(null)
    const playersRef = useRef<any[]>([]);
    const [playingIndex, setPlayingIndex] = useState<number | null>(null);

    const {
        isOpen,
        isOtherPopupOpen,
        images,
        selectedImageIndex,
        closePopup,
        popupInnerData,
        setisOtherPopupOpen,
        setPopupInnerData
    } = usePopupContext()

    const [isFirstSlide, setIsFirstSlide] = useState(true)
    const [isLastSlide, setIsLastSlide] = useState(false)


    const handleSlideChange = (swiper: any) => {
        // Update isFirstSlide and isLastSlide using fullScreenSwiperRef
        if (fullScreenSwiperRef?.current) {
            const swiperInstance = fullScreenSwiperRef.current.swiper;
            setIsFirstSlide(swiperInstance?.isBeginning);
            setIsLastSlide(swiperInstance?.isEnd);
        }

        // Pause and reset all video players
        playersRef.current.forEach((player, i) => {
            if (player) {
                try {
                    const internalPlayer = player.getInternalPlayer?.();
                    internalPlayer?.pause?.(); // Try pausing the internal HTML5 video
                    player.seekTo?.(0); // Reset to beginning
                } catch (e) {
                    console.warn(`Failed to pause/reset player ${i}:`, e);
                }
            }
        });

        // Slight delay helps ReactPlayer re-sync before updating the index
        setTimeout(() => {
            setPlayingIndex(swiper.activeIndex);
        }, 100);
    };

    useEffect(()=> {
		if(imgSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched"
					return prev;
				})
			})
		}
	}, [imgSrc])

	const tl = useRef<GSAPTimeline>(null);
	useEffect(() => {
		if (isPageFetched === "fetched") {
			const html = document.documentElement;

			const timer = setTimeout(() => {
				tl.current = gsap.timeline();
				tl.current
				.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
				.set(".banner .hero-title-word, .banner-head-desc", { yPercent: 50, alpha: 0 })
				.set(".banner .hero-video", { clipPath: "inset(50% 50% 50% 50%)", scale: 0.75, transformOrigin: "center center" })
				.to(".banner .hero-image img", {
					delay: html.classList.contains("loaded") ? 0.8 : 2.2,
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				})
				.to(".banner .hero-image-wrapper", {
					duration: 1,
					ease: "power2.inOut",
					scale: 1,
				}, "-=0.25")
				.to(".navbar_section", {
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				}, "-=0.5")
				.to(".banner .hero-video", {
					duration: 1,
					ease: "expo.inOut",
					scale: 1,
					clipPath: "inset(0% 0% 0% 0%)",
				}, "<")
				.to(".banner .hero-title-word, .banner-head-desc", {
					duration: 1,
					stagger: 0.05,
					ease: "expo.inOut",
					yPercent: 0,
					alpha: 1
				}, "<");
			}, 50);

			return () => clearTimeout(timer);
		}
	}, [isPageFetched]);

    useEffect(() => {
        if (fullScreenSwiperRef.current?.swiper && typeof selectedImageIndex === "number") {
            fullScreenSwiperRef.current.swiper.slideTo(selectedImageIndex);
        }
    }, [selectedImageIndex]);

    return (
        <div className={`${isIpad && "amenities-page"} amenities-page-details`}>
            {imgSrc && (
                <Banner
                    priority
                    imageSrc={`${imgSrc}`}
                    title={data?.sections?.banners[0].title.en}
                    minHeight="89vh"
                    videoSrc={videoSrc}
                    description={data?.sections?.banners[0]?.description?.en}
                    optimizedImage
                />
            )}

            <TitleDescAndSideItems
                title={data?.sections?.overview?.[0]?.title?.en}
                description={
                    data?.sections?.overview?.[0]?.description?.en
                }
                items={data?.sections?.overview?.[0]?.children}
                CustomClass="pt-11 lg:pt-[6.25rem]"
                insideAmenitiesDetailsPage
                optimizedImage
            />

            {data?.sections?.gallery?.[0]?.media && (
                <div
                    className="h-30rem md:h-40rem"
                    style={{ minHeight: isMobile ? "30rem" : "40rem" }}
                >
                    <NeighborhoodsDetailsSwiper
                        images={data?.sections?.gallery?.[0]?.media}
                        optimizedImage
                    />
                </div>
            )}
            {
                data?.sections?.cards?.[0]?.children && (
                    <>
                        <div>
                            <OSectionWithNoImages
                                title={data?.sections?.cards?.[0]?.title?.en}
                                customClass="z-[-1] relative -mb-[15rem] mt-11 lg:mt-[6.25rem] lg:-mb-[25.5rem]"
                                insideAmenitiesDetailsPage
                            />
                        </div>

                        <AccordionSection
                            data={data?.sections?.cards?.[0]?.children}
                            gapValue={42}
                            gapValueMobile={50.6}
                            insideAmenitiesDetailsPage
                            customAccordionClasses="!h-[58rem] lg:!h-[48.8rem]"
                            optimizedImage
                        />
                    </>
                )

            }

            {
                data?.articles?.length > 0 && (
                    <div className="pt-11 lg:pt-[6.25rem] relative z-[55] bg-white">
                        <WhatsHappening data={data?.articles} insideAmenitiesDetailsPage optimizedImage />
                    </div>
                )
            }

            <Popup
                customClassWrapper="popup_half z-[9999] overflow-hidden"
                setIsOpen={closePopup}
                isOpen={isOpen}
                onClose={() => {
                    setPlayingIndex(null);
                    setIsFirstSlide(true);
                    setIsLastSlide(false);
                }}
            >
                <div className={`full-screen-swiper-wrapper relative w-full h-full !py-0`}>
                    <Swiper
                        ref={fullScreenSwiperRef}
                        initialSlide={selectedImageIndex} // Start from the selected image
                        modules={[Navigation, FreeMode]}
                        spaceBetween={0}
                        slidesPerView={1}
                        onSlideChange={handleSlideChange}
                        className="w-full h-full"
                        navigation={{
                            nextEl: ".swiper-full-next",
                            prevEl: ".swiper-full-prev",
                        }}
                        freeMode
                    >
                        {images?.map((item, index) => (
                            <SwiperSlide key={item?.id || index}>
                                <div className="relative w-full h-full bg-black">
                                    {
                                        item?.type !== "video" && (
                                            <ImageWithLoader
                                                src={item.src}
                                                alt={item.alt}
                                                contain
                                                sizeVariant="modal"
                                                quality={85}
                                            />
                                        )
                                    }

                                    {
                                        item?.type === "video" && (
                                            <div className=" h-full relative"
                                            >
                                                <ReactPlayerComp
                                                    key={playingIndex === index ? `playing-${index}` : `idle-${index}`} // force refresh
                                                    ref={(el: any) => (playersRef.current[index] = el)}
                                                    url={item?.src}
                                                    playing={playingIndex === index}
                                                    loop
                                                    width="100%"
                                                    height="100%"
                                                    controls={true}
                                                    playsinline
                                                    className="cursor-pointer"
                                                />
                                            </div>
                                        )
                                    }
                                </div>


                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className="swiper_btns_container absolute w-full flex justify-between top-[50%] translate-y-[-50%] z-[1] px-4
                    pointer-events-none
                    ">
                        <div>
                            <CircleIconButton
                                style="white"
                                icon={<OneDirectionArrow color="white" />}
                                customClass={`scale-x-[-1] swiper-full-prev ${isFirstSlide
                                    ? "fade-out duration-500 opacity-0"
                                    : "fade-in duration-500 opacity-100"
                                    }
                                    pointer-events-auto
                                    `}
                            />
                        </div>
                        <div>
                            <CircleIconButton
                                style="white"
                                icon={<OneDirectionArrow color="white" />}
                                customClass={`swiper-full-next ${isLastSlide
                                    ? "fade-out duration-500 opacity-0"
                                    : "fade-in duration-500 opacity-100"
                                    }
                                    pointer-events-auto
                                    `}
                            />
                        </div>
                    </div>
                    <div
                        className="absolute right-4 bottom-4 p-4 cursor-pointer "
                        style={{
                            borderRadius: "8.125rem",
                            border: "1px solid rgba(252, 252, 252, 0)",
                            background: "rgba(134, 134, 134, 0.5)",
                            boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.04)",
                            backdropFilter: "blur(32px)",
                        }}
                        onClick={closePopup}
                    >
                        <CloseWidthImage />
                    </div>
                </div>

            </Popup>

            <Popup
                customClassWrapper="popup_inline !z-[9988]"
                setIsOpen={setisOtherPopupOpen}
                isOpen={isOtherPopupOpen}
                onClose={() => {
                    setisOtherPopupOpen(false)
                }}
            >
                <MediaCenterDetails
                    data={popupInnerData}
                    fullData={data?.articles}
                    setModalData={setPopupInnerData}
                    insideAmenitiesDetailsPage
                    optimizedImage
                />
            </Popup>
        </div>
    );
};

export default SportsClubPageComponent;